import React from 'react';
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { CommissioningDraft, CommissioningUnitType } from './types';
import { removeFloor, removeUnit } from './draft-state';

function nextProposedNumber(
  entries: Array<{ key: string }>,
  kind: 'floor' | 'unit',
) {
  const keys = new Set(entries.map(({ key }) => key));
  let number = entries.length + 1;
  while (keys.has(`new-${kind}-${number}`)) number += 1;
  return number;
}

export function HierarchyEditor({
  draft,
  onChange,
  readOnly = false,
}: {
  draft: CommissioningDraft;
  onChange: (draft: CommissioningDraft) => void;
  readOnly?: boolean;
}) {
  const editable = draft.status === 'draft' && !readOnly;
  const nextFloorNumber = nextProposedNumber(draft.floors, 'floor');
  const nextUnitNumber = nextProposedNumber(draft.units, 'unit');
  const changeDraft = (nextDraft: CommissioningDraft) => {
    if (editable) onChange(nextDraft);
  };
  const updateFloor = (key: string, name: string) =>
    changeDraft({
      ...draft,
      floors: draft.floors.map((floor) =>
        floor.key === key ? { ...floor, name } : floor,
      ),
    });
  const updateUnit = (
    key: string,
    patch: { name?: string; type?: CommissioningUnitType; floorKey?: string },
  ) =>
    changeDraft({
      ...draft,
      units: draft.units.map((unit) =>
        unit.key === key ? { ...unit, ...patch } : unit,
      ),
    });

  return (
    <Stack spacing={3}>
      <section aria-labelledby="commissioning-floors-heading">
        <Typography id="commissioning-floors-heading" variant="h6">
          Floors
        </Typography>
        <Stack spacing={1}>
          {draft.floors.map((floor) => (
            <Stack key={floor.key} spacing={0.5}>
              <TextField
                disabled={!editable || !!floor.existingFloorId}
                fullWidth
                inputProps={{ 'aria-label': `Floor name ${floor.key}` }}
                label="Floor name"
                onChange={(event) => updateFloor(floor.key, event.target.value)}
                size="small"
                value={floor.name}
              />
              <Typography color="text.secondary" variant="caption">
                Evidence:{' '}
                {floor.evidence.map(({ value }) => value).join(', ') ||
                  'operator supplied'}
              </Typography>
              {!floor.existingFloorId && (
                <Button
                  disabled={!editable}
                  onClick={() =>
                    changeDraft(removeFloor(draft, floor.key).draft)
                  }
                >
                  Remove Floor {floor.name}
                </Button>
              )}
            </Stack>
          ))}
          <Button
            disabled={!editable}
            onClick={() =>
              changeDraft({
                ...draft,
                floors: [
                  ...draft.floors,
                  {
                    key: `new-floor-${nextFloorNumber}`,
                    name: `New Floor ${nextFloorNumber}`,
                    confidence: 1,
                    evidence: [],
                  },
                ],
              })
            }
          >
            Add Floor
          </Button>
        </Stack>
      </section>

      <section aria-labelledby="commissioning-units-heading">
        <Typography id="commissioning-units-heading" variant="h6">
          Units and common areas
        </Typography>
        <Stack spacing={2}>
          {draft.units.map((unit) => (
            <Stack key={unit.key} spacing={1}>
              <TextField
                disabled={!editable || !!unit.existingUnitId}
                inputProps={{ 'aria-label': `Unit name ${unit.key}` }}
                label="Unit name"
                onChange={(event) =>
                  updateUnit(unit.key, { name: event.target.value })
                }
                size="small"
                value={unit.name}
              />
              <TextField
                disabled={!editable || !!unit.existingUnitId}
                label="Unit type"
                onChange={(event) =>
                  updateUnit(unit.key, {
                    type: event.target.value as CommissioningUnitType,
                  })
                }
                select
                size="small"
                value={unit.type}
              >
                <MenuItem value="residential">Residential</MenuItem>
                <MenuItem value="commonArea">Common area</MenuItem>
                <MenuItem value="centralEquipment">Central equipment</MenuItem>
              </TextField>
              <TextField
                disabled={!editable || !!unit.existingUnitId}
                label="Floor"
                onChange={(event) =>
                  updateUnit(unit.key, {
                    floorKey: event.target.value || undefined,
                  })
                }
                select
                size="small"
                value={unit.floorKey ?? ''}
              >
                <MenuItem value="">No floor</MenuItem>
                {draft.floors.map((floor) => (
                  <MenuItem key={floor.key} value={floor.key}>
                    {floor.name}
                  </MenuItem>
                ))}
              </TextField>
              <Typography color="text.secondary" variant="caption">
                Evidence:{' '}
                {unit.evidence.map(({ value }) => value).join(', ') ||
                  'operator supplied'}
              </Typography>
              {!unit.existingUnitId && (
                <Button
                  disabled={!editable}
                  onClick={() => changeDraft(removeUnit(draft, unit.key).draft)}
                >
                  Remove Unit {unit.name}
                </Button>
              )}
            </Stack>
          ))}
          <Button
            disabled={!editable}
            onClick={() =>
              changeDraft({
                ...draft,
                units: [
                  ...draft.units,
                  {
                    key: `new-unit-${nextUnitNumber}`,
                    name: `New Unit ${nextUnitNumber}`,
                    type: 'residential',
                    confidence: 1,
                    evidence: [],
                  },
                ],
              })
            }
          >
            Add Unit
          </Button>
        </Stack>
      </section>
    </Stack>
  );
}
